import ImageIcon from '@mui/icons-material/Image';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import DescriptionIcon from '@mui/icons-material/Description';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import HtmlIcon from '@mui/icons-material/Html';
import LinkIcon from '@mui/icons-material/Link';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import XIcon from '@mui/icons-material/X';
import { Box } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { acceptFileMimeArray, acceptImageMimeObject } from 'utility/constants';
import {
  acceptAudioMimeArray,
  acceptVideoMimeArray,
  urlAllowedImageFileExtensionRegExp,
  urlAllowedAudioFileExtensionRegExp,
} from 'utility/constants';
import { IconHelper } from 'components/generic/IconHelper';
import DOMPurify from 'dompurify';

// Complex Display Components
import {
  InlineHtmlDisplay,
  InlineVideoDisplay,
  InlineArticleDisplay,
  InlineAudioDisplay,
  InlineTwitterDisplay,
} from './components/displays';

export const standardDisplayDimensions: React.CSSProperties = {
  width: 'auto',
  height: 422,
  borderRadius: 4,
  objectFit: 'cover',
};

export const getAssetPreviewImage = (asset: any) => {
  if (!asset) return '';
  const { previews, files, children } = asset;
  return (
    previews?.display?.url ||
    previews?.size2?.url ||
    previews?.size1?.url ||
    files?.preview?.url ||
    files?.image?.url ||
    children?.find((child: any) => child.type === 'image')?.previews?.display?.url ||
    children?.find((child: any) => child.type === 'image')?.previews?.thumbnail?.url ||
    ''
  );
};

export function getInlineAsset(type: string) {
  switch (type) {
    // Related content styles
    case 'relcontent': // For new and placeholder
    case 'bio':
    case 'correction':
    case 'fact':
    case 'highlights':
    case 'info':
    case 'question':
    case 'quote':
    case 'refer':
    case 'update':
      return {
        type: type,
        icon: <IconHelper iconType={type} />,
        edit: (asset: any) => {
          const { title, content, style } = asset;
          return [
            {
              type: 'Select',
              field: 'style',
              label: 'Style',
              value: style,
              required: true,
            },
            {
              type: 'Text',
              field: 'title',
              label: 'blox.editorial.asset.title.label_alt',
              value: title,
            },
            {
              type: 'RichText',
              field: 'content',
              label: 'Content *',
              value: content,
              required: true,
              showCount: 'word',
            },
          ];
        },
        display: (asset: any, theme: Theme) => {
          return (
            <Box
              data-testid="inline-asset-display--relcontent"
              sx={{
                ml: {
                  md: 5,
                },
              }}
            >
              <Box
                component="div"
                data-testid="iad-relcontent-teaser"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(asset?.content) }}
                contentEditable={false}
                sx={(theme) => {
                  const isLightTheme = theme.palette.mode === 'light';
                  return {
                    p: 2,
                    border: `1px solid`,
                    borderColor: theme.blox.colors.gray,
                    borderRadius: 1,
                    backgroundColor: isLightTheme
                      ? theme.blox.colors.white
                      : theme.blox.colors.darkgray,
                    color: isLightTheme
                      ? theme.blox.colors.black
                      : theme.blox.colors.offwhite,
                  };
                }}
              />
            </Box>
          );
        },
      };
    case 'image':
      return {
        type: 'image',
        showContent: true,
        showByline: true,
        icon: (
          <ImageIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.image,
            })}
          />
        ),
        display: (asset: any, theme: Theme) => {
          const { title } = asset;
          const preview = getAssetPreviewImage(asset);

          if (!preview) return null;

          return (
            <img
              data-testid="inline-asset-image"
              // prevent dnd image drop into content as raw <img> element
              draggable={false}
              src={preview}
              alt={title}
              style={{
                ...standardDisplayDimensions,
                width: '100%',
              }}
            />
          );
        },
        edit: (asset: any) => {
          const { title, byline, content, uuid, start_time } = asset;
          if (uuid) {
            return [
              {
                type: 'Text',
                field: 'title',
                label: 'blox.editorial.asset.title.label_alt',
                value: title,
                required: true,
              },
              {
                type: 'RichText',
                field: 'byline',
                label: 'blox.editorial.asset.byline.label',
                value: byline,
                required: false,
              },
              {
                type: 'RichText',
                field: 'content',
                label: 'blox.editorial.asset.caption.label',
                showCount: 'word',
                value: content,
                required: false,
              },
              {
                type: 'Date',
                field: 'start_time',
                label: 'blox.editorial.asset.start_time.label',
                value: start_time,
                required: false,
              },
            ];
          } else {
            return [
              {
                type: 'File',
                allowedMimeTypes: Object.keys(acceptImageMimeObject),
                urlAllowedFileExtensionRegExp: urlAllowedImageFileExtensionRegExp,
                required: true,
              },
            ];
          }
        },
        properties: (asset: any) => {
          const { previews, title } = asset;
          return (
            <img
              data-testid="inline-asset-image-properties"
              draggable={false}
              src={previews?.display?.url}
              alt={title}
              style={{ width: '100%', borderRadius: 4 }}
            />
          );
        },
      };
    case 'audio':
      return {
        type: 'audio',
        fullData: true,
        showContent: true,
        showByline: true,
        icon: (
          <VolumeUpIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.audio,
            })}
          />
        ),
        display: (asset: any, theme: Theme) => {
          return <InlineAudioDisplay {...asset} />;
        },
        edit: (asset: any) => {
          const { title, byline, content, uuid } = asset;
          if (uuid) {
            return [
              {
                type: 'Text',
                field: 'title',
                label: 'blox.editorial.asset.title.label_alt',
                value: title,
                required: true,
              },
              {
                type: 'RichText',
                field: 'byline',
                label: 'blox.editorial.asset.byline.label',
                value: byline,
                required: false,
              },
              {
                type: 'Text',
                field: 'content',
                label: 'blox.editorial.asset.description.label',
                value: content,
                required: false,
              },
            ];
          } else {
            return [
              {
                type: 'File',
                allowedMimeTypes: acceptAudioMimeArray,
                urlAllowedFileExtensionRegExp: urlAllowedAudioFileExtensionRegExp,
                required: true,
              },
            ];
          }
        },
      };
    case 'article':
      return {
        type: 'article',
        icon: (
          <DescriptionIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.article,
            })}
          />
        ),
        display: (asset: any, theme: Theme) => {
          return <InlineArticleDisplay {...asset} />;
        },
        edit: (asset: any) => {
          const { title, byline, summary } = asset;
          return [
            {
              type: 'Text',
              field: 'title',
              label: 'blox.editorial.asset.title.label_alt',
              value: title,
              required: true,
            },
            {
              type: 'RichText',
              field: 'byline',
              label: 'blox.editorial.asset.byline.label',
              value: byline,
              required: false,
            },
            {
              type: 'RichText',
              field: 'summary',
              showCount: 'word',
              label: 'blox.editorial.asset.summary.label',
              value: summary,
              required: false,
            },
          ];
        },
      };
    case 'collection':
      return {
        type: 'collection',
        showContent: true,
        icon: (
          <CollectionsOutlinedIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.collection,
            })}
          />
        ),
        edit: (asset: any) => {
          const { title, byline, content } = asset;
          return [
            {
              type: 'Text',
              field: 'title',
              label: 'blox.editorial.asset.title.label_alt',
              value: title,
              required: true,
            },
            {
              type: 'RichText',
              field: 'byline',
              label: 'blox.editorial.asset.byline.label',
              value: byline,
              required: false,
            },
            {
              type: 'RichText',
              field: 'content',
              label: 'blox.editorial.asset.description.label',
              value: content,
              required: false,
              showCount: 'word',
            },
          ];
        },
      };
    case 'file':
      return {
        type: 'file',
        icon: (
          <AttachFileOutlinedIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.file,
            })}
          />
        ),
        title: (asset: any) => {
          const { title, files } = asset;
          return (
            <a
              href={files?.file?.url}
              target="_blank"
              rel="noreferrer"
            >
              {title}
            </a>
          );
        },
        edit: (asset: any) => {
          const { title, byline, content, uuid } = asset;
          if (uuid) {
            return [
              {
                type: 'Text',
                field: 'title',
                label: 'blox.editorial.asset.title.label_alt',
                value: title,
                required: true,
              },
              {
                type: 'RichText',
                field: 'byline',
                label: 'blox.editorial.asset.byline.label',
                value: byline,
                required: false,
              },
              {
                type: 'RichText',
                field: 'content',
                label: 'blox.editorial.asset.description.label',
                value: content,
                required: false,
                showCount: 'word',
              },
            ];
          } else {
            return [
              {
                type: 'File',
                allowedMimeTypes: acceptFileMimeArray,
                required: true,
              },
            ];
          }
        },
      };
    case 'html':
      return {
        type: 'html',
        fullData: true,
        icon: (
          <HtmlIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.html,
            })}
          />
        ),
        display: (asset: any, theme: Theme) => {
          return <InlineHtmlDisplay {...asset} />;
        },
        edit: (asset: any) => {
          const { title, byline, content, properties } = asset;
          return [
            {
              type: 'Text',
              field: 'title',
              label: 'blox.editorial.asset.title.label_alt',
              value: title,
              required: true,
            },
            {
              type: 'RichText',
              field: 'byline',
              label: 'blox.editorial.asset.byline.label',
              value: byline,
              required: false,
            },
            {
              type: 'Text',
              field: 'content',
              label: 'blox.editorial.asset.description.label',
              value: content,
              required: false,
            },
            {
              type: 'Text',
              field: 'properties.html',
              label: 'blox.html.label',
              value: properties?.html?.trim(),
              required: false,
              multiline: true,
            },
            {
              type: 'Hidden',
              field: 'type',
              value: 'html',
            },
          ];
        },
      };
    case 'link':
      return {
        type: 'link',
        icon: (
          <LinkIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.alink,
            })}
          />
        ),
        title: (asset: any) => {
          const { title, properties } = asset;
          return (
            <a
              href={properties?.link}
              target="_blank"
              rel="noreferrer"
            >
              {title}
            </a>
          );
        },
        edit: (asset: any) => {
          const { title, byline, properties } = asset;
          return [
            {
              type: 'Text',
              field: 'title',
              label: 'blox.editorial.asset.title.label_alt',
              value: title,
              required: true,
            },
            {
              type: 'RichText',
              field: 'byline',
              label: 'blox.editorial.asset.byline.label',
              value: byline,
              required: false,
            },
            {
              type: 'Text',
              field: 'properties.link',
              label: 'blox.block_manager.links.fields.link_url.label',
              value: properties?.link?.trim(),
              required: false,
            },
            {
              type: 'Hidden',
              field: 'type',
              value: 'link',
            },
          ];
        },
      };
    case 'pdf':
      return {
        type: 'pdf',
        showContent: true,
        showByline: true,
        icon: (
          <PictureAsPdfIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.pdf,
            })}
          />
        ),
        display: (asset: any, theme: Theme) => {
          const { title } = asset;
          const preview = getAssetPreviewImage(asset);
          return (
            <>
              {preview && (
                <img
                  src={preview}
                  alt={title}
                  style={{
                    ...standardDisplayDimensions,
                  }}
                />
              )}
            </>
          );
        },
        edit: (asset: any) => {
          const { title, byline, content, uuid } = asset;
          if (uuid) {
            return [
              {
                type: 'Text',
                field: 'title',
                label: 'blox.editorial.asset.title.label_alt',
                value: title,
                required: true,
              },
              {
                type: 'RichText',
                field: 'byline',
                label: 'blox.editorial.asset.byline.label',
                value: byline,
                required: false,
              },
              {
                type: 'Text',
                field: 'content',
                label: 'blox.editorial.asset.description.label',
                value: content,
                required: false,
                multiline: true,
              },
            ];
          } else {
            return [
              {
                type: 'File',
                allowedMimeTypes: ['application/pdf'],
                required: true,
              },
            ];
          }
        },
      };
    case 'poll':
      return {
        type: 'poll',
        icon: (
          <BallotOutlinedIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.poll,
            })}
          />
        ),
        display: (asset: any, theme: Theme) => {
          const { properties } = asset;
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem', // Add a gap between the two columns
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.75rem',
                  '& svg': {
                    fontSize: '1rem',
                  },
                }}
              >
                Graph Type: {properties?.chart_style}
                {properties?.chart_style === 'bar' ? <BarChartIcon /> : <PieChartIcon />}
              </Box>
            </Box>
          );
        },
        edit: (asset: any) => {
          const { title, content } = asset;
          return [
            {
              type: 'Text',
              field: 'title',
              label: 'blox.editorial.asset.title.help_alt_poll',
              value: title,
              required: true,
            },
            {
              type: 'Text',
              field: 'content',
              label: 'blox.editorial.asset.description.label',
              value: content,
              required: false,
            },
          ];
        },
      };
    case 'table':
      return {
        type: 'table',
        fullData: true,
        showContent: true,
        icon: (
          <TableChartOutlinedIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.table,
            })}
          />
        ),
        edit: (asset: any) => {
          const { title, byline, content } = asset;
          return [
            {
              type: 'Text',
              field: 'title',
              label: 'blox.editorial.asset.title.label_alt',
              value: title,
              required: true,
            },
            {
              type: 'RichText',
              field: 'byline',
              label: 'blox.editorial.asset.byline.label',
              value: byline,
              required: false,
            },
            {
              type: 'RichText',
              field: 'content',
              label: 'blox.editorial.asset.description.label',
              value: content,
              required: false,
              showCount: 'word',
            },
          ];
        },
      };
    case 'video':
      return {
        type: 'video',
        fullData: true,
        showContent: true,
        showByline: true,
        icon: (
          <VideocamOutlinedIcon
            sx={(theme) => ({
              color: theme.blox.iconColors.video,
            })}
          />
        ),
        display: (asset: any, theme: Theme) => {
          return <InlineVideoDisplay {...asset} />;
        },
        edit: (asset: any) => {
          const { title, byline, content, uuid } = asset;
          if (uuid) {
            return [
              {
                type: 'Text',
                field: 'title',
                label: 'blox.editorial.asset.title.label_alt',
                value: title,
                required: true,
              },
              {
                type: 'RichText',
                field: 'byline',
                label: 'blox.editorial.asset.byline.label',
                value: byline,
                required: false,
              },
              {
                type: 'RichText',
                field: 'content',
                label: 'blox.editorial.asset.description.label',
                value: content,
                required: false,
                showCount: 'word',
              },
            ];
          } else {
            return [
              {
                type: 'File',
                allowedMimeTypes: acceptVideoMimeArray,
                required: true,
              },
            ];
          }
        },
      };
    case 'twitter':
      return {
        type: 'twitter',
        icon: <XIcon />,
        display: (asset: any, theme: Theme) => {
          return <InlineTwitterDisplay {...asset} />;
        },
        edit: (asset: any) => [],
      };
    case 'removed':
      return {
        type: 'removed',
        icon: (
          <RemoveCircleOutlineIcon
            sx={(theme) => ({
              color: theme.palette.error.main,
            })}
          />
        ),
        display: (asset: any, theme: Theme) => {
          return (
            <Box
              contentEditable={false}
              sx={{
                fontSize: '0.75rem',
                fontWeight: '300',
                gridColumn: '1 / span 2', // Span both columns
                textTransform: 'none',
              }}
            >
              This asset has been removed and is no longer available.
            </Box>
          );
        },
        deleteOnly: true,
      };
    default:
      return {
        type: type,
        icon: <IconHelper iconType={type} />,
        display: (asset: any, theme: Theme) => {
          return (
            <Box
              contentEditable={false}
              sx={{
                fontSize: '0.75rem',
                fontWeight: '300',
                gridColumn: '1 / span 2', // Span both columns
                textTransform: 'none',
              }}
            >
              This asset is a placeholder and is not editable in this editor.
            </Box>
          );
        },
        noEdit: true,
      };
  }
}
